/**
 * 聊天 API 服务层
 * 
 * P0 修复: 添加 credentials: 'include' 以支持 HttpOnly Cookie
 * 
 * [职责]
 * 封装所有与后端聊天相关的 HTTP API 调用，包括：
 * - 会话管理（CRUD）
 * - 流式消息发送（SSE）
 * - HITL 恢复执行
 * - Artifact 更新
 * 
 * [架构]
 * - SSE 传输走 ./sse（fetch + eventsource-parser，无自动重试——POST 非幂等）
 * - 事件分发：将 SSE 事件同时传递给 handleServerEvent（全局处理）和 onChunk 回调（组件级处理）
 * - 自动 Token 刷新：401 时尝试刷新 Token 后重试
 * 
 * [关键函数]
 * - sendMessageStream: 核心流式发送，处理简单/复杂模式
 * - resumeChat: HITL 恢复执行，支持用户修改后的计划
 * - updateArtifact: Artifact 编辑保存
 * 
 * [事件协议]
 * v3.0 新协议事件：
 * - plan.created: 任务计划创建
 * - task.started/completed/failed: 任务状态变更
 * - artifact.generated: 产物生成
 * - message.delta: 流式文本增量
 * - message.done: 消息完成
 * - human.interrupt: HITL 中断等待用户确认
 * 
 * [错误处理]
 * - 网络错误：自动重试一次
 * - 解析错误：跳过无效 SSE 数据，继续处理
 * - 认证错误：触发 Token 刷新或跳转登录
 */

import { fetchSSE, type EventSourceMessage } from './sse'
import type { components } from '@/types/api.generated'

type ChatRequest = components['schemas']['ChatRequest']
type ResumeRequest = components['schemas']['ResumeRequest']
import { getHeaders, buildUrl, handleResponse, handleSSEConnectionError, authenticatedFetch } from './common'
import { ApiMessage, StreamCallback, Thread, StreamRuntimeMeta } from '@/types'
import type { TaskInfo } from '@/types/events'
import { logger } from '@/utils/logger'
import { handleServerEvent } from '@/handlers'
import { useChatStore } from '@/store/chatStore'
import { createSSEPromiseHelpers, SSE_HEARTBEAT_TIMEOUT, SSE_HEARTBEAT_CHECK_INTERVAL } from '@/utils/sseUtils'
import { showLoginDialog } from '@/utils/authUtils'
import type { AnyServerEvent, EventType } from '@/types/events'

// 重新导出类型供外部使用（Thread 类型来自 @/types）
export type { Thread }

// ============================================================================
// SSE 常量配置
// ============================================================================

// 注意：SSE_HEARTBEAT_TIMEOUT 和 SSE_HEARTBEAT_CHECK_INTERVAL 从 sseUtils.ts 导入
//
// 重连策略：POST+SSE 为非幂等请求（自动重发会重复生成/计费，并可能触发 409
// ACTIVE_RUN_CONFLICT），因此网络错误不做自动重发，明确报错由用户决定重试。

class FatalSSEError extends Error {
  status?: number
  code?: string
  details?: unknown

  constructor(message: string, status?: number, code?: string, details?: unknown) {
    super(message)
    this.name = 'FatalSSEError'
    this.status = status
    this.code = code
    this.details = details
  }
}

type StreamRunOptions = {
  url: string
  requestBody: Record<string, unknown>
  errorContext: string
  logPrefix: string
  threadId?: string
  onChunk?: StreamCallback
  abortSignal?: AbortSignal
  resolveOnMessageDone?: boolean
}

function extractErrorStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  const maybe = err as { status?: number; statusCode?: number }
  return maybe.status ?? maybe.statusCode
}

async function extractResponseError(response: Response, fallbackMessage: string): Promise<{
  message: string
  status: number
  code?: string
  details?: unknown
}> {
  try {
    const payload = await response.clone().json()
    return {
      message: payload.error?.message || payload.detail || fallbackMessage,
      status: response.status,
      code: payload.error?.code,
      details: payload.error?.details,
    }
  } catch {
    return {
      message: fallbackMessage,
      status: response.status,
    }
  }
}

function runSSEStream({
  url,
  requestBody,
  errorContext,
  logPrefix,
  threadId,
  onChunk,
  abortSignal,
  resolveOnMessageDone = false,
}: StreamRunOptions): Promise<string> {
  return new Promise((resolve, reject) => {
    let fullContent = ''
    /** 收到过完成标记（[DONE] 或 message.done）才允许 onclose 按成功收尾 */
    let doneMarkerReceived = false
    let activeThreadId = threadId
    let activeRunId: string | undefined
    /**
     * 本次请求的去重作用域（交给 EventHandler）。
     * run id 在 onopen 里由 X-Run-ID 头拿到，先于任何一帧到达，所以正常路径下用它；
     * 拿不到时退化为这次请求的随机 id——**绝不能退化成"整个页面"**：帧 id 是 run 级
     * seq，跨 run 会重号，那样第二个 run 的头部事件会被当成重复丢掉（见 handlers/index.ts
     * 里 UNKNOWN_RUN_SCOPE 的说明）。
     */
    const dedupeScope = `stream:${crypto.randomUUID()}`
    /** B6 断线续传：最近收到的服务端事件 seq（hub 分配的整数 id） */
    let lastSeq = 0
    const ctrl = new AbortController()

    const { safeResolve, safeReject, startHeartbeat, updateActivity, getIsCompleted } = createSSEPromiseHelpers(
      resolve,
      reject,
      {
        timeout: SSE_HEARTBEAT_TIMEOUT,
        checkInterval: SSE_HEARTBEAT_CHECK_INTERVAL,
        onTimeout: () => ctrl.abort(),
        context: errorContext
      }
    )
    startHeartbeat()

    if (abortSignal) {
      abortSignal.addEventListener('abort', () => {
        ctrl.abort()
        safeReject(new Error('请求已取消'))
      })
    }

    // 共享消息处理器：主连接与断线续传连接复用同一套分发/正文流式逻辑
    const handleMessage = async (msg: EventSourceMessage) => {
      updateActivity()

      if (msg.data === '[DONE]') {
        logger.debug(`[chat.ts] ${logPrefix}收到 [DONE]，流式响应完成`)
        doneMarkerReceived = true
        // 传输层收口（评审低危 L6）：服务端已发完成标记，主动断开连接，
        // 不再等服务端关流。先 safeResolve 再 abort（abort 监听里的 safeReject
        // 是单次结算，此时已是 no-op）。
        safeResolve(fullContent)
        ctrl.abort()
        return
      }

      if (msg.data === '' || msg.event === 'heartbeat') {
        return
      }

      // B6：hub 分配的整数 seq，用于断线后续传
      if (msg.id && /^\d+$/.test(msg.id)) {
        lastSeq = parseInt(msg.id, 10)
      }

      try {
        const eventType = msg.event
        const eventData = JSON.parse(msg.data)

        if (eventType) {
          // 会话归属守卫（评审 H5）：流所属会话已不是当前会话（用户切走/返回）时，
          // 该流的任何事件都不得再进全局分发——兜底 addMessage 不带 threadId，
          // 会绕开 chatStore 的 P4-1 守卫，把旧线程的半截回答串进新会话。
          // seq 记录仍保留（上方），onChunk 自有归属守卫，这里切断分发即可。
          const currentThreadId = useChatStore.getState().currentThreadId
          if (activeThreadId && currentThreadId && currentThreadId !== activeThreadId) {
            return
          }
          const runtimeMeta: StreamRuntimeMeta = {
            threadId: activeThreadId,
            runId: activeRunId,
          }
          const fullEvent: AnyServerEvent = {
            id: msg.id || crypto.randomUUID(),
            timestamp: new Date().toISOString(),
            type: eventType as EventType,
            data: eventData
          }

          // 协议 v2 单通道收敛：所有事件统一经 EventHandler（全局唯一分发点），
          // onChunk 只承担两类职责：message.delta 的正文流式、UI 状态同步（runtimeMeta）
          // 去重作用域按 run（run id 未知时按本次请求）：帧 id 是 run 级 seq，跨 run 重号
          const eventScope = activeRunId ?? dedupeScope
          if (eventType === 'message.delta') {
            handleServerEvent(fullEvent, eventScope)
            const content = eventData.content
            if (content && typeof content === 'string' && onChunk) {
              // fullEvent 一并透传：useChatCore 靠事件里的 message_id 路由写入
              // 目标（复杂模式聚合消息=服务端 id，需在尾部创建并 retarget）
              await onChunk(content, activeThreadId, fullEvent, undefined, undefined, runtimeMeta)
              fullContent += content
            }
          } else {
            handleServerEvent(fullEvent, eventScope)
            // artifact.generated 需一并透给 onChunk：useChatCore 里已有「产物到达即
            // 防抖刷新右栏画布/画廊」的逻辑，但此前该事件到不了 onChunk（只透传
            // message.* 与 error），那段逻辑因此是死代码——运行期间画布不刷新，
            // 产物要等 run 结束才出现。
            if (
              onChunk &&
              (eventType.startsWith('message.') ||
                eventType === 'error' ||
                eventType === 'artifact.generated')
            ) {
              await onChunk(undefined, activeThreadId, fullEvent, undefined, undefined, runtimeMeta)
            }
          }

          if (resolveOnMessageDone && eventType === 'message.done') {
            doneMarkerReceived = true
            logger.debug(`[chat.ts] ${logPrefix}收到 message.done，流结束`)
            safeResolve(fullContent)
          }
        }
      } catch {
        logger.debug(`[chat.ts] ${logPrefix}解析 SSE 数据失败，跳过:`, msg.data.substring(0, 100))
      }
    }

    /**
     * B6 断线续传：从 lastSeq 之后重放并跟随剩余事件。
     * 返回是否接管了连接（true = 终态处理归 resume 流负责）。
     */
    const attemptResume = (): Promise<boolean> => {
      return new Promise((resolveHandover) => {
        if (!activeRunId || !activeThreadId || lastSeq <= 0) {
          resolveHandover(false)
          return
        }
        let opened = false
        fetchSSE(buildUrl(`/chat/${activeThreadId}/stream/resume?last_event_id=${lastSeq}`), {
          method: 'GET',
          headers: { ...getHeaders(), Accept: 'text/event-stream' },
          signal: ctrl.signal,
          async onopen(response) {
              if (!response.ok) {
                resolveHandover(false)
                throw new FatalSSEError('resume unavailable', response.status)
              }
              opened = true
              updateActivity()
              resolveHandover(true)
              logger.debug(`[chat.ts] ${logPrefix}断线续传连接已建立 (after seq=${lastSeq})`)
            },
            onmessage: (msg) => handleMessage(msg),
            onerror(err) {
              if (opened) {
                safeReject(err instanceof Error ? err : new Error('连接中断，请重试'))
                return
              }
              resolveHandover(false)
            },
            onclose() {
              if (doneMarkerReceived) {
                safeResolve(fullContent)
                return
              }
              if (opened) {
                safeReject(new Error('连接中断，回答可能不完整，请重试'))
              }
            },
          }
        ).catch(() => {
          /* 已通过 resolveHandover/safeReject 结算，静默 */
        })
      })
    }

    fetchSSE(url, {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(requestBody),
      signal: ctrl.signal,

      async onopen(response) {
        if (!response.ok) {
          const parsedError = await extractResponseError(
            response,
            `SSE 连接失败: ${response.status}`
          )
          const fatalError = new FatalSSEError(
            parsedError.message,
            parsedError.status,
            parsedError.code,
            parsedError.details,
          )
          safeReject(fatalError)
          ctrl.abort()
          throw fatalError
        }
        handleSSEConnectionError(response, errorContext)
        updateActivity()
        logger.debug(`[chat.ts] ${logPrefix}SSE 连接已建立`)

        const responseThreadId = response.headers.get('X-Thread-ID')
        const responseRunId = response.headers.get('X-Run-ID')
        if (responseThreadId) {
          activeThreadId = responseThreadId
        }
        if (responseRunId) {
          activeRunId = responseRunId
        }
        if (onChunk && (responseThreadId || responseRunId)) {
          const runtimeMeta: StreamRuntimeMeta = {
            threadId: activeThreadId,
            runId: activeRunId,
          }
          await onChunk(undefined, activeThreadId, undefined, undefined, undefined, runtimeMeta)
        }
      },

      onmessage: handleMessage,

      onerror(err: unknown) {
        const errorLike = err as { name?: string }
        if (errorLike.name === 'AbortError' || ctrl.signal.aborted) {
          logger.debug(`[chat.ts] ${logPrefix}请求已取消`)
          safeReject(new Error('请求已取消'))
          return
        }

        const status = extractErrorStatus(err)
        if (status === 401) {
          logger.warn(`[chat.ts] ${logPrefix}SSE 收到 401 错误，触发登录弹窗`)
          showLoginDialog()
          safeReject(err instanceof Error ? err : new Error('认证失败'))
          return
        }

        if (status !== undefined && status >= 400 && status < 500) {
          logger.error(`[chat.ts] ${logPrefix}SSE 收到 ${status} 客户端错误，终止:`, err)
          safeReject(err instanceof Error ? err : new Error(`客户端错误: ${status}`))
          return
        }

        // POST+SSE 非幂等：传输层无自动重试（sse.ts 刻意设计）。优先尝试
        // 断点续传（GET，只读重放）：成功则交接后续事件处理；失败回到
        // "终止 + 提示重试"的原语义
        if (!doneMarkerReceived && activeRunId && activeThreadId && lastSeq > 0) {
          logger.warn(`[chat.ts] ${logPrefix}SSE 网络错误，尝试断点续传 (after seq=${lastSeq})`)
          void attemptResume().then((handover) => {
            if (!handover) {
              safeReject(err instanceof Error ? err : new Error('连接异常，请重试'))
            }
          })
          return
        }

        logger.error(`[chat.ts] ${logPrefix}SSE 网络错误，终止连接:`, err)
        safeReject(err instanceof Error ? err : new Error('连接异常，请重试'))
      },

      onclose() {
        logger.debug(`[chat.ts] ${logPrefix}SSE 连接已关闭`)
        if (getIsCompleted()) return
        if (doneMarkerReceived) {
          safeResolve(fullContent)
          return
        }
        // 无完成标记的关闭 = 服务端异常断流：按错误处理，而非把截断内容当成功
        logger.warn(`[chat.ts] ${logPrefix}连接关闭但未收到完成标记，内容可能被截断`)
        safeReject(new Error('连接中断，回答可能不完整，请重试'))
      },
    }).catch((swallowErr: unknown) => {
      // onopen 抛出的 FatalSSEError（!ok/认证失败）在此收口；错误已由 safeReject 结算
      logger.debug(`[chat.ts] ${logPrefix}主连接终止:`, swallowErr)
    })
  })
}

// ============================================================================
// API 函数
// ============================================================================

/**
 * 分页会话列表响应
 */
export interface PaginatedThreads {
  items: Thread[]
  total: number
  page: number
  limit: number
  pages: number
}

/**
 * 获取会话列表（支持分页）
 * @param page 页码（从1开始）
 * @param limit 每页条数（默认20）
 */
export async function getThreads(page: number = 1, limit: number = 20): Promise<PaginatedThreads> {
  const response = await authenticatedFetch(
    buildUrl(`/threads?page=${page}&limit=${limit}`), 
    { headers: getHeaders() }
  )
  return handleResponse<PaginatedThreads>(response, '获取会话列表失败')
}

/**
 * 获取单个会话详情（包含完整消息）
 */
export async function getThread(id: string): Promise<Thread> {
  const response = await authenticatedFetch(buildUrl(`/threads/${id}`), {
    headers: getHeaders()
  })
  return await handleResponse<Thread>(response, '获取会话详情失败')
}

/**
 * 获取会话消息列表（单独端点，P0-5 优化）
 */

export async function deleteThread(id: string): Promise<void> {
  const response = await authenticatedFetch(buildUrl(`/threads/${id}`), {
    method: 'DELETE',
    headers: getHeaders()
  })
  return handleResponse<void>(response, '删除会话失败')
}

/**
 * 批量删除会话
 */
export interface BatchDeleteResult {
  success: boolean
  deleted_count: number
  failed_ids: string[]
}

export type { ChatDocument } from '@/components/chat/types'
import type { ChatDocument } from '@/components/chat/types'

export async function sendMessage(
  messages: ApiMessage[],
  agentId: string = 'default-chat',
  onChunk?: StreamCallback,
  threadId?: string | null,
  abortSignal?: AbortSignal,
  assistantMessageId?: string | undefined,  // v3.0: 前端传递的助手消息 ID
  images?: string[] | undefined,  // v3.4.7: 当前轮图片输入（dataURL，仅视觉模型）
  documents?: ChatDocument[] | undefined  // v3.5: 附件文档（后端解析为文本注入上下文）
): Promise<string> {

  const history = messages.slice(0, -1)
  const lastMessage = messages[messages.length - 1]
  const messageContent = lastMessage.content

  const url = buildUrl('/chat')

  // 非流式分支已删：全站唯一调用链恒传 onChunk（流式是产品语义），
  // 后端 sync 端点保留为公开 API 语义（stream=false 可用）。

  const requestBody: ChatRequest = {
    message: messageContent,
    images: images ?? [],
    documents: documents ?? [],
    history: history.map(m => ({ role: m.role, content: m.content })),
    agent_id: agentId,
    thread_id: threadId,
    stream: true,
    message_id: assistantMessageId,
  }

  return runSSEStream({
    url,
    requestBody,
    errorContext: 'chat.ts',
    logPrefix: '',
    threadId: threadId || undefined,
    onChunk,
    abortSignal,
  })
}

/**
 * 更新 Artifact 内容（持久化到后端）
 * 用于用户编辑 AI 生成的产物
 */

export interface ResumeChatParams {
  threadId: string
  runId: string
  planVersion: number
  updatedPlan?: TaskInfo[] // 🔥 任务依赖关系（depends_on 关键字段）
  approved: boolean
  /** 显式动作：approve（批准）/ revise（驳回+反馈 → 专家修订 v(n+1)）/ terminate（终止）。
   * 缺省按 approved 推导，保持旧语义。 */
  action?: 'approve' | 'revise' | 'terminate'
  /** 驳回反馈（revise/terminate 时提交，后端落库为会话 user 消息） */
  feedback?: string
}

/** wire 请求体：形状由后端 ResumeRequest 锚定。updated_plan 在后端是
 * dict[str, Any] 列表，前端 TaskInfo 是它的结构化视图——边界处显式断言。 */
function _resumeBody(params: ResumeChatParams): ResumeRequest {
  return {
    thread_id: params.threadId,
    run_id: params.runId,
    plan_version: params.planVersion,
    updated_plan: params.updatedPlan as ResumeRequest['updated_plan'],
    approved: params.approved,
    action: params.action,
    feedback: params.feedback,
  }
}

export async function resumeChat(
  params: ResumeChatParams,
  onChunk?: StreamCallback,
  abortSignal?: AbortSignal
): Promise<string> {
  const url = buildUrl('/chat/resume')
  
  // 如果不需要流式响应（如用户取消），使用普通 fetch。
  // 用 authenticatedFetch（与全站一致）：裸 fetch 会绕过统一的 401 静默刷新（评审低危 L6）
  if (!onChunk) {
    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(_resumeBody(params)),
      signal: abortSignal,
      // P0 修复: 允许携带 Cookie
      credentials: 'include'
    })
    const result = await handleResponse<unknown>(response, '恢复执行失败')
    return typeof result === 'string' ? result : JSON.stringify(result)
  }

  // 🔥 流式响应：复用与 sendMessage 完全相同的 SSE 处理逻辑
  // 🔥 心跳检测：超时处理，防止 Promise 无限等待
  return runSSEStream({
    url,
    requestBody: _resumeBody(params),
    errorContext: 'chat.ts resume',
    logPrefix: 'Resume ',
    threadId: params.threadId,
    onChunk,
    abortSignal,
    resolveOnMessageDone: true,
  })
}

export async function cancelRun(runId: string): Promise<{ status: string; message: string }> {
  const response = await authenticatedFetch(buildUrl(`/runs/${runId}/cancel`), {
    method: 'POST',
    headers: getHeaders(),
  })
  return handleResponse<{ status: string; message: string }>(response, '取消运行失败')
}
